import React, { useEffect, useState } from 'react';
import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { AppPasswordInput, OutlineButton, PrimaryButton } from '../../../components/ui';
import { useAuth } from '../../../contexts/AuthContext';
import { changeStaffPassword } from '../../../repositories/staff.repository';
import {
  STAFF_PASSWORD_MIN_LENGTH,
  validateStaffPassword,
} from '../../../utils/staffPassword';

export function StaffPasswordModal({ isOpen, onClose, staff, onSaved }) {
  const toast = useToast();
  const { isSuperAdmin } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Super admin reset from Staff Management — no current password.
  // Non–super-admin can only change own password and must prove current.
  const requireCurrentPassword = !isSuperAdmin;

  useEffect(() => {
    if (!isOpen) return;
    setCurrentPassword('');
    setPassword('');
    setConfirmPassword('');
    setErrors({});
    setSaving(false);
  }, [isOpen, staff?.id, staff?.staffId]);

  if (!staff) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    const next = validateStaffPassword(password, confirmPassword);
    if (requireCurrentPassword && !currentPassword.trim()) {
      next.currentPassword = 'Current password is required';
    }
    setErrors(next);
    if (Object.keys(next).length) {
      toast({ title: 'Fix highlighted fields', status: 'warning', position: 'top-right' });
      return;
    }

    if (staff.id == null) {
      toast({
        title: 'Missing staff id',
        status: 'error',
        position: 'top-right',
      });
      return;
    }

    setSaving(true);
    try {
      await changeStaffPassword(staff.id, {
        newPassword: password,
        ...(requireCurrentPassword ? { currentPassword: currentPassword.trim() } : {}),
      });
      toast({
        title: 'Password updated',
        description: `${staff.fullName} · ${staff.staffId}`,
        status: 'success',
        position: 'top-right',
      });
      onSaved?.();
      onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to change password',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        as="form"
        onSubmit={handleSave}
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        overflow="hidden"
      >
        <ModalHeader pb={2}>Update password</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          <VStack align="stretch" spacing={4}>
            <Box
              p={3}
              bg="bg.input"
              borderRadius="sm"
              borderWidth="1px"
              borderColor="border.subtle"
            >
              <Text fontWeight="600" fontSize="15px">
                {staff.fullName}
              </Text>
              <Text fontSize="13px" color="text.secondary" fontFamily="mono" mt={0.5}>
                {staff.email} · {staff.staffId}
              </Text>
              {isSuperAdmin ? (
                <Text fontSize="12px" color="text.secondary" mt={2}>
                  Super admin reset — current password not required.
                </Text>
              ) : null}
            </Box>
            <SimpleGrid columns={1} spacing={4}>
              {requireCurrentPassword ? (
                <AppPasswordInput
                  label="Current password"
                  isRequired
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    setErrors((prev) => {
                      const n = { ...prev };
                      delete n.currentPassword;
                      return n;
                    });
                  }}
                  isInvalid={!!errors.currentPassword}
                  errorMessage={errors.currentPassword}
                  autoComplete="current-password"
                />
              ) : null}
              <AppPasswordInput
                label="New password"
                isRequired
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors((prev) => {
                    const n = { ...prev };
                    delete n.password;
                    if (confirmPassword) delete n.confirmPassword;
                    return n;
                  });
                }}
                placeholder={`Min ${STAFF_PASSWORD_MIN_LENGTH} characters`}
                isInvalid={!!errors.password}
                errorMessage={errors.password}
                autoComplete="new-password"
              />
              <AppPasswordInput
                label="Confirm password"
                isRequired
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setErrors((prev) => {
                    const n = { ...prev };
                    delete n.confirmPassword;
                    return n;
                  });
                }}
                isInvalid={!!errors.confirmPassword}
                errorMessage={errors.confirmPassword}
                autoComplete="new-password"
              />
            </SimpleGrid>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} borderTopWidth="1px" borderColor="border.subtle">
          <OutlineButton type="button" onClick={onClose} isDisabled={saving}>
            Cancel
          </OutlineButton>
          <PrimaryButton type="submit" isLoading={saving}>
            Update password
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
